// client/src/components/AppInstall.js
// PWA install (iOS/Android) + Android APK download buttons
import React, { useState, useEffect } from 'react';
import { CURRENT_APP_VERSION } from '../config/appVersion';

// Android APK URL (hosted on the server, served by nginx from a persistent path)
export const ANDROID_APK_URL = '/app/rummy.apk';

const AppInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(null);

  // Check for a newer app version (APK) on load
  useEffect(() => {
    const checkForUpdates = async () => {
      try {
        const res = await fetch('/app/latest.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (data.version && data.version !== CURRENT_APP_VERSION) {
          setUpdateAvailable(data);
        }
      } catch (e) {
        // ignore network errors
      }
    };
    checkForUpdates();
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent;
    setIsIOS(/iphone|ipad|ipod/i.test(ua) && !window.MSStream);

    // Already running as an installed (standalone) app?
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsInstalled(standalone);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isInstalled) return;
    if (!deferredPrompt) {
      // No native prompt (e.g. iOS Safari) → show manual instructions
      setShowIOSHelp((v) => !v);
      return;
    }
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  if (isInstalled) return null;

  return (
    <div className="app-install" style={styles.wrap}>
      {updateAvailable && (
        <div style={styles.updateBanner}>
          <strong>🔄 Update available</strong> — version {updateAvailable.version} is ready.
          <a href={updateAvailable.apkUrl || ANDROID_APK_URL} style={styles.updateLink}>Download update</a>
        </div>
      )}

      <div style={styles.divider}>
        <span style={styles.dividerLine} />
        <span style={styles.dividerText}>Get the app</span>
        <span style={styles.dividerLine} />
      </div>

      {/* PWA install (native prompt on Android/Chrome; manual help on iOS) */}
      <button style={styles.primary} onClick={handleInstall}>
        {isIOS ? '📲 Install on iPhone / iPad' : '📲 Install App'}
      </button>

      {showIOSHelp && (
        <div style={styles.help}>
          <p style={{ margin: 0 }}>1. Tap the <strong>Share</strong> button (square with arrow) in Safari</p>
          <p style={{ margin: '4px 0 0' }}>2. Scroll and tap <strong>Add to Home Screen</strong></p>
          <p style={{ margin: '4px 0 0' }}>3. Tap <strong>Add</strong> — the JV Games icon appears on your home screen</p>
        </div>
      )}

      {/* Android APK direct download */}
      {!isIOS && (
        <a href={ANDROID_APK_URL} download style={styles.secondary}>
          🤖 Download Android App (.apk)
        </a>
      )}
    </div>
  );
};

const styles = {
  wrap: { marginTop: '20px', textAlign: 'center' },
  divider: { display: 'flex', alignItems: 'center', gap: '10px', margin: '12px 0' },
  dividerLine: { flex: 1, height: '1px', background: '#d7dde2' },
  dividerText: { fontSize: '12px', color: '#8a94a0', whiteSpace: 'nowrap' },
  primary: {
    display: 'inline-block', width: '100%', padding: '12px', border: 'none',
    borderRadius: '8px', background: '#0e6b45', color: '#fff',
    fontWeight: 600, fontSize: '14px', cursor: 'pointer'
  },
  secondary: {
    display: 'inline-block', width: '100%', marginTop: '10px', padding: '12px',
    borderRadius: '8px', border: '1px solid #0e6b45', color: '#0e6b45',
    fontWeight: 600, fontSize: '14px', textDecoration: 'none', boxSizing: 'border-box'
  },
  help: {
    marginTop: '10px', padding: '12px', background: '#f4f7f5',
    borderRadius: '8px', fontSize: '13px', color: '#34495e', textAlign: 'left'
  },
  updateBanner: {
    marginBottom: '10px', padding: '12px', background: '#fff3cd',
    borderRadius: '8px', fontSize: '13px', color: '#664d03', textAlign: 'left',
    border: '1px solid #ffe08a'
  },
  updateLink: {
    display: 'inline-block', marginTop: '6px', color: '#0e6b45', fontWeight: 600, textDecoration: 'underline'
  }
};

export default AppInstall;
